import { DEFAULT_BEREICH, DEFAULT_DOCUMENT_STATUS, type Bereich } from '@manager/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { DocumentScanner } from './DocumentScanner'
import { HochladenDialog, type HochladenBilanz } from './HochladenDialog'
import { NoteIcon } from './icons'
import { ALLE_TRAY_FELDER, PageTray, type TrayDetails, type TrayField } from './PageTray'
import { ApiRequestError } from '../lib/api'
import { DATEI_ACCEPT } from '../lib/dateitypen'
import { einlesen } from '../lib/einlesen'
import {
  useCategories,
  useHouseholdUsers,
  useUploadDocument,
  type UploadDetails,
} from '../lib/documents'
import {
  buildUpload,
  cameraAvailable,
  createPage,
  defaultScanTitle,
  pageFromFile,
  releasePage,
  rotatePage,
  type ScanPage,
} from '../lib/scan/pages'

interface UploadState {
  running: number
  message: string | null
}

/**
 * Woher die nächste Seite kommt, wenn im Stapel „Weitere Seite" gewählt wird.
 *
 * `foto` ist die Kamera, die sofort aufgeht; `kamera-app` der Umweg über die
 * Auswahl des Systems, den es nur als Rückweg gibt, wenn der eigene Scanner
 * nicht laufen kann.
 */
type PageSource = 'scanner' | 'foto' | 'kamera-app'

/** Ein leerer Stapel: kein Titel, unsortiert, für beide, pendent. */
const EMPTY_DETAILS: TrayDetails = {
  title: '',
  categoryId: null,
  assignedTo: null,
  status: DEFAULT_DOCUMENT_STATUS,
}

/**
 * Die Wege, auf denen ein Dokument über diese Seite in die App kommt:
 *
 *  - Dokument scannen – der eingebaute Scanner mit Randerkennung
 *  - Foto aufnehmen – die Kamera geht sofort auf, das Bild bleibt wie es ist;
 *    in der DocBase steht an dieser Stelle „Notiz erstellen", siehe `onNotiz`
 *  - Datei wählen – PDFs und Screenshots, vor dem Hochladen eine nach der
 *    anderen mit Titel, Kategorie, Status usw. (`HochladenDialog`)
 *  - App-Verknüpfung `?aufnehmen=1` – langer Druck auf das App-Symbol
 *
 * Geteiltes aus anderen Apps kommt nicht hier an, sondern auf der Seite
 * `/teilen` (screens/Share.tsx): Dort wird es mit Titel, Kategorie und Status
 * abgelegt, bevor es in der Liste steht.
 *
 * „Foto aufnehmen" und „Datei wählen" waren lange fast dasselbe: Beide
 * öffneten die Auswahl des Systems, in der die Kamera nur einer von mehreren
 * Einträgen war. Jetzt trennen sie sich sauber – der eine Weg macht ein Bild,
 * der andere sucht eines. Und der Zweck des ersten ist nicht das Blatt Papier,
 * dafür gibt es den Scanner: Es ist die Kinderzeichnung, das Zeugnis an der
 * Wand, alles, was man ablegen will, ohne es zurechtzuschneiden.
 *
 * Aufnahmen aus den Kamera-Wegen gehen nicht direkt in die Ablage, sondern
 * zuerst in einen Stapel. Erst wenn alle Seiten beisammen sind, wird daraus ein
 * Dokument – ein zweiseitiger Brief bleibt so ein Brief.
 */
export function UploadControls({
  bereich = DEFAULT_BEREICH,
  felder = ALLE_TRAY_FELDER,
  akzent = 'bg-brand-800',
  onNotiz,
  onCheckliste,
}: {
  /** In welche Sammlung das Hochgeladene gehört. */
  bereich?: Bereich
  /** Welche Angaben im Stapel abgefragt werden. */
  felder?: readonly TrayField[]
  /**
   * Farbe des runden Knopfes unten rechts. Er ist der auffälligste Punkt der
   * Seite und gehört deshalb der App, in der er steht – marineblau im
   * Haushalt, petrol in der DocBase.
   */
  akzent?: string
  /**
   * Wo eine Notiz statt eines Fotos gehört, steht sie an dessen Platz.
   *
   * Die DocBase reicht das durch: Sie sammelt Wissen, keine Schnappschüsse –
   * ein Foto ist dort weder ein Blatt Papier (dafür gibt es den Scanner) noch
   * ein Gedanke dazu (dafür jetzt die Notiz). Fehlt die Angabe, bleibt es beim
   * Foto, so wie im Haushalt.
   */
  onNotiz?: () => void
  /** Daneben eine Checkliste – auch in einer Sammlung steht manches zum Abhaken. */
  onCheckliste?: () => void
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const upload = useUploadDocument()
  // Kategorien und Personen ändern sich praktisch nie und liegen deshalb
  // ohnehin im Zwischenspeicher der Dokumentenliste – für den Stapel entsteht
  // dadurch keine zusätzliche Anfrage.
  const categories = useCategories(bereich)
  const users = useHouseholdUsers()

  const photoRef = useRef<HTMLInputElement>(null)
  const cameraAppRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [state, setState] = useState<UploadState>({ running: 0, message: null })

  const [pages, setPages] = useState<ScanPage[]>([])
  const [details, setDetails] = useState<TrayDetails>(EMPTY_DETAILS)
  /** Gewählte Dateien vor dem Hochladen – dazu, was sich nicht lesen liess. */
  const [vorlage, setVorlage] = useState<{ files: File[]; hinweise: string[] } | null>(null)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [preparing, setPreparing] = useState(false)
  const [source, setSource] = useState<PageSource>('scanner')

  const uploadFiles = useCallback(
    async (files: File[], documentDetails?: UploadDetails): Promise<boolean> => {
      if (files.length === 0) return false
      setState({ running: files.length, message: null })

      // Zuerst alle einlesen, dann hochladen (siehe `lib/einlesen.ts`): Eine
      // Datei aus Google Drive, an die Chrome nicht herankommt, meldet sich so
      // mit einem klaren Satz statt als Verbindungsfehler – und bei mehreren
      // ist die letzte noch lesbar, wenn sie an die Reihe kommt.
      const { dateien, probleme: problems } = await einlesen(files)
      let done = 0

      for (const file of dateien) {
        try {
          await upload.mutateAsync({ file, bereich, ...documentDetails })
          done += 1
        } catch (error) {
          if (error instanceof ApiRequestError && error.code === 'duplicate') {
            problems.push(`${file.name}: liegt bereits in der Ablage`)
          } else {
            problems.push(`${file.name}: ${error instanceof Error ? error.message : 'Fehler'}`)
          }
        }
        setState((previous) => ({ ...previous, running: previous.running - 1 }))
      }

      setState({
        running: 0,
        message:
          problems.length === 0
            ? `${done} ${done === 1 ? 'Dokument' : 'Dokumente'} hinzugefügt.`
            : problems.join(' · '),
      })

      return problems.length === 0
    },
    [upload, bereich],
  )

  // Verknüpfung vom Startbildschirm: direkt in den Scanner.
  const capture = searchParams.get('aufnehmen')
  useEffect(() => {
    if (capture !== '1') return
    setSearchParams(
      (params) => {
        params.delete('aufnehmen')
        return params
      },
      { replace: true },
    )

    if (cameraAvailable()) {
      setSource('scanner')
      setScannerOpen(true)
    } else {
      openCameraApp()
    }
  }, [capture, setSearchParams])

  // Beim Verlassen der Seite bleiben sonst so viele blob:-Adressen im
  // Speicher, wie Seiten im Stapel lagen.
  const pagesRef = useRef<readonly ScanPage[]>(pages)
  pagesRef.current = pages
  useEffect(
    () => () => {
      for (const page of pagesRef.current) releasePage(page)
    },
    [],
  )

  function discardPages() {
    for (const page of pages) releasePage(page)
    setPages([])
    setDetails(EMPTY_DETAILS)
  }

  function removePage(id: string) {
    const page = pages.find((entry) => entry.id === id)
    if (page) releasePage(page)
    setPages((current) => current.filter((entry) => entry.id !== id))
  }

  function movePage(id: string, direction: -1 | 1) {
    setPages((current) => {
      const index = current.findIndex((entry) => entry.id === id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= current.length) return current

      const next = [...current]
      const moved = next[index] as ScanPage
      next[index] = next[target] as ScanPage
      next[target] = moved
      return next
    })
  }

  /**
   * Eine Seite um eine Vierteldrehung drehen.
   *
   * Die gedrehte Seite tritt an die Stelle der alten, mit derselben Kennung –
   * sie bleibt also, wo sie im Stapel steht. Die alte Vorschau-Adresse wird
   * erst danach freigegeben: Gäbe man sie vorher frei, zeigte die Liste für
   * einen Wimpernschlag ein leeres Bild.
   */
  async function rotate(id: string) {
    const page = pages.find((entry) => entry.id === id)
    if (!page) return

    setPreparing(true)
    try {
      const gedreht = await rotatePage(page)
      setPages((current) => current.map((entry) => (entry.id === id ? gedreht : entry)))
      releasePage(page)
    } catch {
      setState({ running: 0, message: 'Die Seite liess sich nicht drehen.' })
    } finally {
      setPreparing(false)
    }
  }

  function openScanner() {
    setMenuOpen(false)
    setSource('scanner')
    setScannerOpen(true)
  }

  /** Die Kamera für ein Bild, das kein Blatt Papier ist. */
  function openPhotoCamera() {
    setMenuOpen(false)
    setSource('foto')
    setScannerOpen(false)
    photoRef.current?.click()
  }

  /**
   * Der Rückweg, wenn der eigene Scanner nicht laufen kann: die Auswahl des
   * Systems. Sie führt zur Kamera-App mit all ihren Modi – auch dem
   * Dokumentenmodus, den die meisten mitbringen und der hier das Nächste ist,
   * was es zum ausgefallenen Scanner gibt.
   */
  function openCameraApp() {
    setMenuOpen(false)
    setSource('kamera-app')
    setScannerOpen(false)
    cameraAppRef.current?.click()
  }

  /**
   * Fotos aus der Kamera-App in den Stapel übernehmen.
   *
   * Was sich im Browser nicht öffnen lässt – auf manchen Geräten HEIC – wird
   * unverändert hochgeladen. Ins PDF könnte es nicht eingebettet werden, aber
   * als eigenes Dokument ist es allemal besser aufgehoben als in einer
   * Fehlermeldung.
   */
  async function collectPhotos(files: File[]) {
    setPreparing(true)
    const prepared: ScanPage[] = []
    const undecodable: File[] = []

    for (const file of files) {
      try {
        prepared.push(await pageFromFile(file))
      } catch {
        undecodable.push(file)
      }
    }

    if (prepared.length > 0) setPages((current) => [...current, ...prepared])
    setPreparing(false)

    if (undecodable.length > 0) await uploadFiles(undecodable)
  }

  async function uploadPages() {
    const chosen = details.title.trim() || defaultScanTitle()

    let file: File
    try {
      file = await buildUpload(pages, chosen)
    } catch (error) {
      setState({
        running: 0,
        message:
          error instanceof Error ? error.message : 'Die Seiten liessen sich nicht zusammenfügen.',
      })
      return
    }

    // Nur bei Erfolg verwerfen: Reisst die Verbindung ab, bleibt der Stapel
    // samt Angaben erhalten und ein zweiter Versuch kostet keine neue Aufnahme.
    if (await uploadFiles([file], { ...details, title: chosen })) discardPages()
  }

  function handleCameraInput(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    setMenuOpen(false)
    void collectPhotos(files)
  }

  /**
   * Gewählte Dateien: zuerst einlesen (siehe `lib/einlesen.ts`), dann eine
   * nach der anderen vorlegen – mit den Angaben, unter denen sie abgelegt
   * wird (`HochladenDialog`).
   */
  async function dateienVorlegen(files: File[]) {
    if (files.length === 0) return
    setState({ running: files.length, message: null })
    const { dateien, probleme } = await einlesen(files)
    if (dateien.length === 0) {
      setState({ running: 0, message: probleme.join(' · ') })
      return
    }
    setState({ running: 0, message: null })
    setVorlage({ files: dateien, hinweise: probleme })
  }

  function vorlageFertig({ hochgeladen, ausgelassen, hinweise }: HochladenBilanz) {
    setVorlage(null)
    const teile: string[] = []
    if (hochgeladen > 0) {
      const nicht = ausgelassen > 0 ? `, ${ausgelassen} nicht hochgeladen` : ''
      teile.push(
        `${hochgeladen} ${hochgeladen === 1 ? 'Dokument' : 'Dokumente'} hinzugefügt${nicht}.`,
      )
    }
    teile.push(...hinweise)
    setState({ running: 0, message: teile.length > 0 ? teile.join(' ') : null })
  }

  function handleFileInput(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    setMenuOpen(false)
    void dateienVorlegen(files)
  }

  const busy = state.running > 0 || preparing

  return (
    <>
      {state.message ? (
        <button
          onClick={() => setState((previous) => ({ ...previous, message: null }))}
          className="fixed inset-x-4 bottom-40 z-30 mx-auto block max-w-md rounded-xl bg-slate-900 px-4 py-3 text-center text-sm text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
          role="status"
        >
          {state.message}
        </button>
      ) : null}

      {/* capture='environment': Ein Griff auf „Foto aufnehmen" soll die
          Rückkamera öffnen und sonst nichts. Ohne das Attribut erscheint auf
          Android erst die Auswahl des Systems – dieselbe, die auch „Datei
          wählen" zeigt, und damit zwei Einträge im Menü, die dasselbe tun. */}
      <input
        ref={photoRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleCameraInput}
      />
      {/* Dasselbe ohne capture, nur für den Rückweg aus dem Scanner: Dort
          führt die Auswahl des Systems zur Kamera-App samt Dokumentenmodus –
          und um ein Blatt Papier geht es in dem Moment ja gerade. */}
      <input
        ref={cameraAppRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleCameraInput}
      />
      <input
        ref={fileRef}
        type="file"
        multiple
        accept={DATEI_ACCEPT}
        className="hidden"
        onChange={handleFileInput}
      />

      {scannerOpen ? (
        <DocumentScanner
          pageCount={pages.length}
          onCapture={(blob) => {
            // Die blob:-Adresse entsteht bewusst ausserhalb von setPages –
            // React ruft die Funktion darin unter Umständen zweimal auf, und
            // die zweite Adresse würde niemand mehr freigeben.
            const page = createPage(blob)
            setPages((current) => [...current, page])
          }}
          onClose={() => setScannerOpen(false)}
          onFallback={openCameraApp}
        />
      ) : pages.length > 0 ? (
        <PageTray
          pages={pages}
          details={details}
          onDetailsChange={setDetails}
          defaultTitle={defaultScanTitle()}
          categories={categories.data?.categories ?? []}
          users={users.data?.users ?? []}
          felder={felder}
          bereich={bereich}
          akzent={akzent}
          busy={busy}
          onAddPage={() => {
            if (source === 'scanner') setScannerOpen(true)
            else if (source === 'foto') photoRef.current?.click()
            else cameraAppRef.current?.click()
          }}
          onRemove={removePage}
          onMove={movePage}
          onRotate={(id) => void rotate(id)}
          onDiscard={() => {
            if (window.confirm(`${pages.length === 1 ? 'Die Seite' : 'Alle Seiten'} verwerfen?`)) {
              discardPages()
            }
          }}
          onUpload={() => void uploadPages()}
        />
      ) : null}

      {vorlage ? (
        <HochladenDialog
          files={vorlage.files}
          hinweise={vorlage.hinweise}
          bereich={bereich}
          felder={felder}
          // Fälligkeiten kennt nur der Haushalt.
          faellig={bereich !== 'docbase'}
          akzent={akzent}
          onFertig={vorlageFertig}
        />
      ) : null}

      {menuOpen ? (
        <>
          <button
            className="fixed inset-0 z-20 bg-slate-900/20"
            onClick={() => setMenuOpen(false)}
            aria-label="Menü schliessen"
          />
          <div className="fixed bottom-40 right-4 z-30 flex flex-col items-end gap-2">
            <MenuAction label="Dokument scannen" onClick={openScanner}>
              <svg className="size-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
                  <path d="M4 12h16" />
                </g>
              </svg>
            </MenuAction>
            {onNotiz ? (
              <MenuAction
                label="Notiz erstellen"
                onClick={() => {
                  setMenuOpen(false)
                  onNotiz()
                }}
              >
                <NoteIcon className="size-5" />
              </MenuAction>
            ) : (
              <MenuAction label="Foto aufnehmen" onClick={openPhotoCamera}>
                <FotoIcon />
              </MenuAction>
            )}
            {onCheckliste ? (
              <MenuAction
                label="Checkliste erstellen"
                onClick={() => {
                  setMenuOpen(false)
                  onCheckliste()
                }}
              >
                <svg className="size-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 6.5 5.5 8 8 5M4 12.5 5.5 14 8 11M4 18.5 5.5 20 8 17M11 6.5h9M11 12.5h9M11 18.5h9"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </MenuAction>
            ) : null}
            <MenuAction label="Datei wählen" onClick={() => fileRef.current?.click()}>
              <svg className="size-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M14 3H7a1.5 1.5 0 0 0-1.5 1.5v15A1.5 1.5 0 0 0 7 21h10a1.5 1.5 0 0 0 1.5-1.5V7.5L14 3Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path d="M13.5 3.5V8h4.5" stroke="currentColor" strokeWidth="1.8" />
              </svg>
            </MenuAction>
          </div>
        </>
      ) : null}

      <button
        onClick={() => setMenuOpen((open) => !open)}
        disabled={busy}
        // data-hinzufuegen: Der Knopf weicht einem aufgeklappten Menü, siehe
        // useOpenPanel und die Regel dazu in index.css.
        data-hinzufuegen=""
        className={`fixed bottom-20 right-4 z-30 grid size-14 place-items-center rounded-full text-white shadow-lg transition active:scale-95 disabled:opacity-70 ${akzent}`}
        aria-label={busy ? 'Wird hochgeladen' : 'Dokument hinzufügen'}
        aria-expanded={menuOpen}
      >
        {busy ? (
          <svg className="size-6 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        ) : (
          <svg
            className={`size-7 transition-transform ${menuOpen ? 'rotate-45' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
      </button>
    </>
  )
}

function MenuAction({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className="flex min-h-11 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium shadow-lg dark:bg-slate-800"
    >
      {children}
      {label}
    </button>
  )
}

/** Die Kamera – „Foto aufnehmen". */
function FotoIcon() {
  return (
    <svg className="size-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1.2-2h6.6L16.5 7h2A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5v-9Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  )
}
